import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, CheckCircle, AlertCircle } from "lucide-react";
import api from "../../utils/Api";

const ACCENT = "#FF7A00";
const NAVY   = "#0F172A";
const MUTED  = "#64748B";
const BORDER = "#E2E8F0";

function ResetPassword() {
  const [email, setEmail]         = useState("");
  const [errors, setErrors]       = useState({});
  const [message, setMessage]     = useState("");
  const [success, setSuccess]     = useState(false);
  const [loading, setLoading]     = useState(false);

  const validate = () => {
    const e = {};
    if (!email.trim()) e.email = "El email es obligatorio.";
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = "El email no es válido.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      await api.post("/reset-password", { email });
      setSuccess(true);
      setMessage("Correo de recuperación enviado. Revisa tu bandeja de entrada.");
      setEmail("");
    } catch {
      setSuccess(false);
      setMessage("Hubo un problema al enviar el correo. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* Panel izquierdo */}
      <div className="tn-auth-left" style={{
        flex: "0 0 48%",
        background: "linear-gradient(145deg, #0F1629 0%, #1A2347 50%, #1E3A8A 100%)",
        display: "flex", flexDirection: "column", padding: "48px 56px",
        position: "relative", overflow: "hidden",
      }}>
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.07 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="diagRP" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse" patternTransform="rotate(25)">
              <line x1="0"  y1="0" x2="0"  y2="120" stroke="white" strokeWidth="1"/>
              <line x1="40" y1="0" x2="40" y2="120" stroke="white" strokeWidth="1"/>
              <line x1="80" y1="0" x2="80" y2="120" stroke="white" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diagRP)" />
        </svg>
        <div style={{
          position: "absolute", top: "-60px", right: "-60px",
          width: "280px", height: "280px",
          background: "radial-gradient(circle, rgba(255,122,0,0.15) 0%, transparent 70%)",
          pointerEvents: "none"
        }} />

        <div style={{ position: "relative", zIndex: 1, marginBottom: "auto" }}>
          <svg width="56" height="56" viewBox="0 0 64 64" fill="none" style={{ marginBottom: "40px" }}>
            <line x1="32" y1="4"  x2="32" y2="60" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="4"  y1="32" x2="60" y2="32" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="11" y1="11" x2="53" y2="53" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="53" y1="11" x2="11" y2="53" stroke="white" strokeWidth="7" strokeLinecap="round"/>
          </svg>

          <h2 style={{
            fontSize: "clamp(32px, 3.5vw, 46px)", fontWeight: "800",
            color: "#FFFFFF", lineHeight: "1.1", letterSpacing: "-1.5px",
            margin: "0 0 16px", fontStyle: "italic"
          }}>
            Sin problema.<br />
            <span style={{ color: ACCENT }}>Te ayudamos.</span>
          </h2>
          <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.55)", lineHeight: "1.7", maxWidth: "300px", margin: 0 }}>
            Te enviamos un enlace a tu correo para que puedas restablecer tu contraseña en segundos.
          </p>
        </div>

        <p style={{ position: "relative", zIndex: 1, fontSize: "12px", color: "rgba(255,255,255,0.25)", margin: 0, marginTop: "48px" }}>
          © 2026 Turnora. Todos los derechos reservados.
        </p>
      </div>

      {/* Panel derecho */}
      <div style={{
        flex: 1, background: "#FFFFFF",
        display: "flex", flexDirection: "column", padding: "40px 56px",
      }}>
        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "auto" }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FF7A00" class="bi bi-clock-history me-2 align-middle" viewBox="0 0 16 16"><path d="M8.515 1.019A7 7 0 1 0 15 8a.5.5 0 0 0-1 0 6 6 0 1 1-5.485-6.981z"></path><path d="M7.5 3a.5.5 0 0 1 .5.5v4.25l3.15 1.85a.5.5 0 1 1-.5.866L7.5 8.5V3.5A.5.5 0 0 1 7.5 3z"></path></svg>
          <span style={{ fontSize: "16px", fontWeight: "700", color: NAVY, letterSpacing: "-0.3px" }}>Turnora</span>
        </div>

        {/* Form */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", maxWidth: "360px", width: "100%" }}>

          {/* Icono */}
          <div style={{
            width: "56px", height: "56px", borderRadius: "14px",
            background: "#FFF7ED", display: "flex", alignItems: "center",
            justifyContent: "center", marginBottom: "24px"
          }}>
            <Mail size={26} style={{ color: ACCENT }} />
          </div>

          <h1 style={{ fontSize: "28px", fontWeight: "800", color: NAVY, margin: "0 0 8px", letterSpacing: "-0.8px" }}>
            Recuperar contraseña
          </h1>
          <p style={{ fontSize: "14px", color: MUTED, margin: "0 0 32px", lineHeight: "1.6" }}>
            Introduce tu correo y te enviaremos instrucciones para restablecerla.
          </p>

          {message && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              padding: "12px 14px", marginBottom: "24px",
              background: success ? "#F0FDF4" : "#FEF2F2",
              border: `1px solid ${success ? "#BBF7D0" : "#FECACA"}`,
              borderRadius: "8px"
            }}>
              {success
                ? <CheckCircle size={16} style={{ color: "#10B981", flexShrink: 0, marginTop: "1px" }} />
                : <AlertCircle  size={16} style={{ color: "#EF4444", flexShrink: 0, marginTop: "1px" }} />
              }
              <span style={{ fontSize: "13px", color: success ? "#15803D" : "#7F1D1D", fontWeight: "500" }}>
                {message}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div style={{ marginBottom: "32px" }}>
              <label style={{ fontSize: "11px", fontWeight: "700", color: MUTED, letterSpacing: "0.8px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>
                Correo electrónico
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hola@turnora.es"
                autoComplete="email"
                style={{
                  width: "100%", padding: "10px 4px",
                  background: "transparent", border: "none",
                  borderBottom: `2px solid ${errors.email ? "#EF4444" : BORDER}`,
                  fontSize: "15px", color: NAVY, outline: "none",
                  fontFamily: "inherit", transition: "border-color 0.15s"
                }}
                onFocus={(e) => { if (!errors.email) e.target.style.borderBottomColor = ACCENT; }}
                onBlur={(e)  => { e.target.style.borderBottomColor = errors.email ? "#EF4444" : BORDER; }}
              />
              {errors.email && <p style={{ fontSize: "12px", color: "#EF4444", margin: "6px 0 0", fontWeight: "500" }}>{errors.email}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%", height: "52px",
                background: loading ? "#CBD5E1" : NAVY,
                color: "white", border: "none", borderRadius: "10px",
                fontSize: "15px", fontWeight: "700",
                cursor: loading ? "not-allowed" : "pointer",
                fontFamily: "inherit", transition: "background 0.15s",
                letterSpacing: "-0.2px", marginBottom: "20px"
              }}
              onMouseEnter={(e) => !loading && (e.currentTarget.style.background = "#1E2947")}
              onMouseLeave={(e) => !loading && (e.currentTarget.style.background = loading ? "#CBD5E1" : NAVY)}
            >
              {loading ? "Enviando..." : "Enviar instrucciones"}
            </button>
          </form>

          <p style={{ textAlign: "center", fontSize: "13px", color: MUTED, margin: 0 }}>
            <Link to="/" style={{ color: ACCENT, fontWeight: "600", textDecoration: "none" }}>
              ← Volver al inicio de sesión
            </Link>
          </p>
        </div>

        <div style={{ marginTop: "auto" }} />
      </div>

      <style>{`
        @media (max-width: 860px) { .tn-auth-left { display: none !important; } }
      `}</style>
    </div>
  );
}

export default ResetPassword;
